import React, { useEffect, useState } from 'react';
import { useSpotify } from '../../context/SpotifyContext';
import './SpotifyPlayer.css';

export const SpotifyPlayer: React.FC = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const {
    isAuthenticated,
    isPremium,
    user,
    login,
    logout,
    isReady,
    isPlaying,
    currentTrack,
    pause,
    resume,
    error,
    loading,
  } = useSpotify();

  useEffect(() => {
    if (!isAuthenticated) {
      setIsDismissed(false);
    }
  }, [isAuthenticated]);

  if (isDismissed) {
    return null;
  }

  // Not authenticated - show login button
  if (!isAuthenticated) {
    return (
      <div className="spotify-player not-authenticated">
        <div className="spotify-login-prompt">
          <span className="spotify-icon">🎧</span>
          <div className="spotify-login-text">
            <strong>Spotify Premium</strong>
            <small>Speel nummers direct af via Spotify</small>
          </div>
          <div className="spotify-user-actions">
            <button 
              className="spotify-login-btn"
              onClick={login}
              disabled={loading}
            >
              {loading ? 'Laden...' : 'Inloggen'}
            </button>
            <button onClick={() => setIsDismissed(true)} className="logout-btn small" title="Sluiten">×</button>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated but not Premium
  if (!isPremium) {
    return (
      <div className="spotify-player not-premium">
        <div className="spotify-warning">
          <span className="warning-icon">⚠️</span>
          <div>
            <strong>Spotify Premium vereist</strong>
            <small>De Web Playback SDK werkt alleen met een Premium account.</small>
          </div>
          <div className="spotify-user-actions">
            <button onClick={logout} className="logout-btn">Uitloggen</button>
            <button onClick={() => setIsDismissed(true)} className="logout-btn small" title="Sluiten">×</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`spotify-player authenticated ${isReady ? '' : 'connecting'}`}>
      {/* User info */}
      <div className="spotify-user">
        <span className={`spotify-icon ${isReady ? 'connected' : 'connecting'}`}>
          {isReady ? '🎧' : '⏳'}
        </span>
        <div className="user-info">
          <strong>{user?.name || 'Spotify'}</strong>
          <small>{isReady ? '✅ Klaar om af te spelen' : '⚠️ Verbinden met Spotify...'}</small>
        </div>
        <div className="spotify-user-actions">
          <button onClick={logout} className="logout-btn">Uitloggen</button>
          <button onClick={() => setIsDismissed(true)} className="logout-btn small" title="Sluiten">×</button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="spotify-error">
          {error}
        </div>
      )}

      {/* Mini player */}
      {currentTrack && (
        <div className="mini-player">
          <div className="track-info">
            <span className="track-name">{currentTrack.name}</span>
            <span className="track-artist">{currentTrack.artist}</span>
          </div>
          <div className="player-controls">
            <button 
              className="play-pause-btn"
              onClick={() => isPlaying ? pause() : resume()}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact play button for use in track lists
export const SpotifyPlayButton: React.FC<{
  trackUri: string;
  trackName?: string;
}> = ({ trackUri, trackName }) => {
  const { isAuthenticated, isPremium, isReady, play, isPlaying, currentTrack, pause } = useSpotify();

  if (!isAuthenticated || !isPremium || !isReady) {
    return null;
  }

  const isThisTrack = currentTrack?.uri === trackUri;

  const handleClick = async () => {
    if (isThisTrack && isPlaying) {
      await pause();
    } else {
      await play(trackUri);
    }
  };

  return (
    <button 
      className="spotify-play-btn"
      onClick={handleClick}
      title={trackName ? `Speel ${trackName}` : 'Afspelen met Spotify'}
    >
      {isThisTrack && isPlaying ? '⏸️' : '▶️'} Spotify
    </button>
  );
};
